import { AxiosInstance } from 'axios';
import { stringify } from 'query-string';

import { REPORT_FILTERS_KEYS } from '@reports/application/configs/reportKeysConst';
import { REPORT_ENDPOINTS } from '@reports/application/configs/endpoints';

import type { AuthService } from 'types/AuthService';
import type {
  FilterRequestApiDto,
  FilterResponseApiDto,
  ApiBlockDataRequest,
  ApiTableBlockResponse,
  ApiChartBlockResponse,
  ApiSummaryCardBlockResponse,
} from './types';
import type { ReportsHttpClientType } from './types/ReportsHttpClientType';

/**
 * Creates an HTTP client for Reports API operations
 *
 * Pure HTTP client that only handles API requests/responses with their own types.
 *
 * @param props - Configuration object
 * @param props.httpClient - Axios instance for HTTP requests
 * @param props.authService - Authentication service to get store UUID
 * @returns ReportsHttpClient instance with API methods
 *
 * @example
 * const httpClient = createReportsHttpClient({
 *   httpClient: axiosInstance,
 *   authService: authServiceInstance,
 * });
 *
 * const response = await httpClient.fetchFilterCatalog({ filters: ['brands', 'providers'] });
 */
export const createReportsHttpClient = (props: {
  httpClient: AxiosInstance;
  authService: AuthService;
}): ReportsHttpClientType => {
  const { httpClient, authService } = props;

  /**
   * Fetches filter catalog from API
   *
   * @param request - API request with filter keys
   * @returns Promise with API response containing filter options
   * @throws {Error} When store UUID is not available
   */
  const fetchFilterCatalog = async (request: FilterRequestApiDto): Promise<FilterResponseApiDto> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to fetch filter catalog');
    }

    const response = await httpClient.post<FilterResponseApiDto>(REPORT_ENDPOINTS.FILTERS, {
      storeUuids: [storeUuid],
      filterEntities: request.filters,
    });

    return response.data;
  };

  /**
   * Fetches table block data from API
   *
   * @param request - API request with block data filters and optional pagination
   * @param endpoint - Specific endpoint for the table block
   * @returns Promise with API response containing table data
   * @throws {Error} When store UUID is not available
   */
  const fetchTableBlock = async (request: ApiBlockDataRequest, endpoint: string): Promise<ApiTableBlockResponse> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to fetch table block data');
    }

    // Build query params for pagination if present
    let fullEndpoint = endpoint;
    if (request.page && request.pageSize) {
      const queryParams = `?page=${request.page}&pageSize=${request.pageSize}`;
      fullEndpoint = `${endpoint}${queryParams}`;
    }

    const response = await httpClient.post<ApiTableBlockResponse>(fullEndpoint, {
      filters: {
        storeUuids: [storeUuid],
        startDate: `${request.startDate} ${request.startHour}`,
        endDate: `${request.endDate} ${request.endHour}`,
        brandUuids: request.filters[REPORT_FILTERS_KEYS.BRAND],
        providers: request.filters[REPORT_FILTERS_KEYS.PROVIDER],
        orderTypeNameUuids: request.filters[REPORT_FILTERS_KEYS.ORDER_TYPE],
        userPaymentUuids: request.filters[REPORT_FILTERS_KEYS.USER_PAYMENT],
        userOrderUuids: request.filters[REPORT_FILTERS_KEYS.USER_ORDER],
        storePaymentTypeUuids: request.filters[REPORT_FILTERS_KEYS.STORE_PAYMENT_TYPE],
        userCanceledUuids: request.filters[REPORT_FILTERS_KEYS.USER_CANCELED],
        userAuthorizedUuids: request.filters[REPORT_FILTERS_KEYS.USER_AUTHORIZED],
      },
    });

    return response.data;
  };

  /**
   * Fetches chart block data from API
   *
   * @param request - API request with block data filters
   * @param endpoint - Specific endpoint for the chart block
   * @returns Promise with API response containing chart data
   * @throws {Error} When store UUID is not available
   */
  const fetchChartBlock = async (request: ApiBlockDataRequest, endpoint: string): Promise<ApiChartBlockResponse> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to fetch chart block data');
    }

    const response = await httpClient.post<ApiChartBlockResponse>(endpoint, {
      filters: {
        storeUuids: [storeUuid],
        startDate: `${request.startDate} ${request.startHour}`,
        endDate: `${request.endDate} ${request.endHour}`,
        brandUuids: request.filters[REPORT_FILTERS_KEYS.BRAND],
        providers: request.filters[REPORT_FILTERS_KEYS.PROVIDER],
        orderTypeNameUuids: request.filters[REPORT_FILTERS_KEYS.ORDER_TYPE],
        userPaymentUuids: request.filters[REPORT_FILTERS_KEYS.USER_PAYMENT],
        userOrderUuids: request.filters[REPORT_FILTERS_KEYS.USER_ORDER],
        storePaymentTypeUuids: request.filters[REPORT_FILTERS_KEYS.STORE_PAYMENT_TYPE],
        userCanceledUuids: request.filters[REPORT_FILTERS_KEYS.USER_CANCELED],
        userAuthorizedUuids: request.filters[REPORT_FILTERS_KEYS.USER_AUTHORIZED],
      },
    });

    return response.data;
  };

  /**
   * Fetches summary card block data from API
   *
   * @param request - API request with block data filters
   * @param endpoint - Specific endpoint for the summary card block
   * @returns Promise with API response containing summary card data
   * @throws {Error} When store UUID is not available
   */
  const fetchSummaryCardBlock = async (
    request: ApiBlockDataRequest,
    endpoint: string,
  ): Promise<ApiSummaryCardBlockResponse> => {
    const storeUuid = authService?.user?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to fetch summary card block data');
    }

    const queryParams = stringify({ store_uuid: storeUuid });

    const response = await httpClient.post<ApiSummaryCardBlockResponse>(`${endpoint}/?${queryParams}`, request);

    return response.data;
  };

  /**
   * Fetches export report as a blob file
   *
   * Makes a POST request to the export endpoint with slugs array and returns the file as a blob.
   * This is a synchronous download - the file is generated on the server and returned immediately.
   *
   * @param request - API request with block data filters and slugs array
   * @param endpoint - Export endpoint (e.g., 'api/v1/sales/export')
   * @returns Promise with file blob
   * @throws {Error} When store UUID is not available
   */
  const fetchExportReport = async (
    request: ApiBlockDataRequest & { queries: string[] },
    endpoint: string,
  ): Promise<Blob> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to export report');
    }

    const response = await httpClient.post<Blob>(
      endpoint,
      {
        queries: request.queries,
        filters: {
          storeUuids: [storeUuid],
          startDate: `${request.startDate} ${request.startHour}`,
          endDate: `${request.endDate} ${request.endHour}`,
          brandUuids: request.filters[REPORT_FILTERS_KEYS.BRAND],
          providers: request.filters[REPORT_FILTERS_KEYS.PROVIDER],
          orderTypeNameUuids: request.filters[REPORT_FILTERS_KEYS.ORDER_TYPE],
          userPaymentUuids: request.filters[REPORT_FILTERS_KEYS.USER_PAYMENT],
          userOrderUuids: request.filters[REPORT_FILTERS_KEYS.USER_ORDER],
          storePaymentTypeUuids: request.filters[REPORT_FILTERS_KEYS.STORE_PAYMENT_TYPE],
          userCanceledUuids: request.filters[REPORT_FILTERS_KEYS.USER_CANCELED],
          userAuthorizedUuids: request.filters[REPORT_FILTERS_KEYS.USER_AUTHORIZED],
        },
      },
      {
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      },
    );
    return response.data;
  };

  return {
    fetchFilterCatalog,
    fetchTableBlock,
    fetchChartBlock,
    fetchSummaryCardBlock,
    fetchExportReport,
  };
};
